import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { PAYS, trouverPays, type Pays } from './api'
import { injecterStyles } from './styles'

/** Sans accents ni majuscules : « cote » trouve « Côte d'Ivoire ». */
export function simplifier(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

/** Pays correspondant à une recherche : nom, code ISO ou indicatif (« 223 », « +223 »). */
export function filtrerPays(recherche: string, liste: readonly Pays[] = PAYS): Pays[] {
  const q = simplifier(recherche.trim()).replace(/^\+/, '')
  if (!q) return [...liste]
  return liste.filter((p) => simplifier(p.nom).includes(q) || p.iso.toLowerCase() === q || p.indicatif.startsWith(q))
}

/**
 * Sélecteur de pays — SANS drapeau (nom + indicatif), avec recherche. Combobox accessible : flèches, Entrée,
 * Échap ; le focus revient au bouton à la fermeture.
 */
export function SelecteurPays({
  iso,
  surChoix,
  desactive = false,
}: {
  iso: string
  surChoix: (p: Pays) => void
  desactive?: boolean
}) {
  injecterStyles()
  const [ouvert, setOuvert] = useState(false)
  const [recherche, setRecherche] = useState('')
  const [actif, setActif] = useState(0)
  const idListe = useId()
  const racine = useRef<HTMLDivElement | null>(null)
  const bouton = useRef<HTMLButtonElement | null>(null)
  const champ = useRef<HTMLInputElement | null>(null)
  const courant = trouverPays(iso) ?? PAYS[0]!
  const resultats = useMemo(() => filtrerPays(recherche), [recherche])

  useEffect(() => {
    if (!ouvert) return
    champ.current?.focus()
    const dehors = (e: MouseEvent) => {
      if (racine.current && !racine.current.contains(e.target as Node)) setOuvert(false)
    }
    document.addEventListener('mousedown', dehors)
    return () => document.removeEventListener('mousedown', dehors)
  }, [ouvert])

  function fermer() {
    setOuvert(false)
    setRecherche('')
    setActif(0)
    bouton.current?.focus()
  }

  function choisir(p: Pays) {
    surChoix(p)
    fermer()
  }

  return (
    <div ref={racine} style={{ position: 'relative' }}>
      <button
        ref={bouton}
        type="button"
        className="cw-pays-bouton"
        aria-haspopup="listbox"
        aria-expanded={ouvert}
        aria-controls={idListe}
        aria-label={`Pays : ${courant.nom}, indicatif +${courant.indicatif}. Changer de pays`}
        disabled={desactive}
        onClick={() => setOuvert((o) => !o)}
      >
        <span>{courant.nom}</span>
        <span className="cw-indicatif">+{courant.indicatif}</span>
      </button>
      {ouvert && (
        <div className="cw-liste">
          <input
            ref={champ}
            className="cw-entree cw-recherche"
            type="search"
            placeholder="Rechercher un pays"
            aria-label="Rechercher un pays"
            value={recherche}
            onChange={(e) => {
              setRecherche(e.target.value)
              setActif(0)
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setActif((a) => Math.min(a + 1, resultats.length - 1))
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setActif((a) => Math.max(a - 1, 0))
              } else if (e.key === 'Enter') {
                e.preventDefault()
                const p = resultats[actif]
                if (p) choisir(p)
              } else if (e.key === 'Escape') {
                e.preventDefault()
                fermer()
              }
            }}
          />
          <div id={idListe} role="listbox" aria-label="Pays">
            {resultats.length === 0 && <p className="cw-aide" style={{ padding: 8 }}>Aucun pays ne correspond.</p>}
            {resultats.map((p, i) => (
              <button
                key={p.iso}
                type="button"
                role="option"
                aria-selected={i === actif || p.iso === iso}
                className="cw-option"
                onMouseEnter={() => setActif(i)}
                onClick={() => choisir(p)}
              >
                <span>{p.nom}</span>
                <span className="cw-indicatif">+{p.indicatif}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
